/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { StackedBarChart } from 'src/core/components/StackedBarChart';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getScopedTheme, getStylist, joinClassNames } from 'src/core/themes/luisStylist';
import { LocalizationProvider } from 'src/core/utils/contexts/LocalizationProvider';
import { BarTooltip } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/BarTooltip/BarTooltip';
import { getDistributionChartConfig } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/distributionChartHelpers';
import { Legend } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/Legend';
import { SortDropdown } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/SortDropdown';
import { AnalyticsDropdown } from 'src/luis/projects/analytics/components/TrainingEvaluation/AnalyticsDropdown';
import { useStateRef } from 'src/luis/projects/analytics/hooks/useStateRef';
import { SortOptions } from 'src/luis/projects/analytics/types/SortOptions';

const messages = defineMessages({
    utterancesNumberOpt: {
        id: 'analytics.predictionsPerIntentCard.utterancesNumberOpt',
        defaultMessage: 'Utterances (number)'
    },
    utterancesPercentOpt: {
        id: 'analytics.predictionsPerIntentCard.utterancesPercentOpt',
        defaultMessage: 'Utterances (percentage)'
    },
    utterancesDropdownLabel: {
        id: 'analytics.predictionsPerIntentCard.utterancesDropdownLabel',
        defaultMessage: 'Toggle between utterances percentage and number'
    },
    sortDropdownLabel: {
        id: 'analytics.predictionsPerIntentCard.sortDropdownLabel',
        defaultMessage: 'Choose sort type'
    }
});

export type YAxisMode = 'number' | 'percent';

const { getClassName } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');

const rootClassName = getClassName('StyledAnalyticsDropdown', theme => ({
    border: 'none',
    height: theme.dropdownLargeHeight
}));

const boldRootClassName = getClassName('StyledAnalyticsDropdown', theme => ({
    fontWeight: theme.boldFont as Property.FontWeight
}));

const titleClassName = getClassName('Title', theme => ({
    paddingLeft: 0,
    fontSize: theme.labelFontSize,
    height: theme.dropdownLargeHeight
}));

/**
 * Displays a bar chart which illustrates the distribution of the intents across
 * The application and overview of each intent.
 */
export const PredictionsPerIntentCard = observer(() => {
    const { localize, locale } = useLocalization();
    const [sortDropdownTitle, setSortDropdownTitle] = React.useState(SortOptions.none);
    const [sortType, setSortType, sortTypeRef] = useStateRef<SortOptions>(SortOptions.none);
    const [selectedYAxisType, setSelectedYAxisType] = React.useState<YAxisMode>('percent');
    const { rootStore } = useGlobalState();
    const { isXXLargeOrGreater } = useResponsiveMode();
    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;
    const eventLoggerRef = React.useRef(getEventLogger('PredictionsPerIntentCard'));

    const yAxisOptions: IDropdownOption[] = [
        { key: 'percent', text: localize(messages.utterancesPercentOpt), selected: true },
        { key: 'number', text: localize(messages.utterancesNumberOpt) }
    ];

    const groups = ['correct', 'unclear', 'incorrect'];
    const chartConfigurations = getDistributionChartConfig(selectedYAxisType === 'number', sortType, analyticsStore);

    const utterancesPercentageDropDownChange = (option: IDropdownOption) => {
        eventLoggerRef.current.log('UtterancePercentageDropdownSelectionChange');
        setSelectedYAxisType(option.key as YAxisMode);
    };

    const sortIntentsDropDownChange = (option: IDropdownOption) => {
        eventLoggerRef.current.log('SortIntentsDropdownSelectionChange');
        setSortType(option.key as SortOptions);
        // We should reassign the sortDropdownTitle to make sure that
        // the dropdown would re-render to make the title unchanged.
        setTimeout(() => setSortDropdownTitle(SortOptions.none), 0);
    };

    return (
        <Stack padding={dashboardTheme.detailedFaultyIntentPercentLegendSpacing}>
            <Stack horizontal={isXXLargeOrGreater}>
                <StackItem align="start">
                    <AnalyticsDropdown
                        isSearchEnabled={false}
                        options={yAxisOptions}
                        defaultSelectedKey={selectedYAxisType}
                        ariaLabel={localize(messages.utterancesDropdownLabel)}
                        onRenderCaretDown={() => <Icon iconName="CaretDownSolid8" />}
                        onChange={(_, option) => utterancesPercentageDropDownChange(option)}
                        styles={{ title: titleClassName, root: joinClassNames(rootClassName, boldRootClassName) }}
                    />
                </StackItem>
                <Stack grow={isXXLargeOrGreater} horizontalAlign="end">
                    <SortDropdown
                        ariaLabel={localize(messages.sortDropdownLabel)}
                        selected={sortDropdownTitle}
                        yAxisMode={selectedYAxisType}
                        onChange={sortIntentsDropDownChange}
                    />
                </Stack>
            </Stack>
            <StackItem grow={1}>
                <StackedBarChart
                    {...chartConfigurations}
                    groups={groups}
                    dataToFocus={groups.filter(group => sortType !== SortOptions.none && group === sortType)}
                    onRenderTooltip={index => (
                        // LocalizationProvider is used because this function get called outside
                        // the scope of the LuisProvider that wraps LUIS because it is
                        // invoked through C3jx.
                        <LocalizationProvider locale={locale}>
                            <BarTooltip
                                index={index}
                                isNumber={selectedYAxisType === 'number'}
                                sortType={sortTypeRef.current}
                                utterancesPerIntent={analyticsStore.utterancesPerIntent}
                            />
                        </LocalizationProvider>
                    )}
                />
            </StackItem>
            <Legend sortType={sortType} setSortType={setSortType} />
        </Stack>
    );
});
